import React, { useState, useEffect } from 'react';
import Home from './portals/Home';
import OnboardingPortal from './portals/Onboarding';
import GeneralApply from './portals/GeneralApply';
import { API } from './config';
import { ToastContainer } from './components/UI';
import { useToast } from './hooks/useToast';
import { Link, SiteFooter } from './components/Site';

// ── Interviewer Assessment Portal (public, accessed via ?assess=TOKEN) ──────
const ASSESS_PARAMS = [
  { id: 'personality',       label: 'Personality / Appearance / Manners' },
  { id: 'communication',     label: 'Communication / Interpersonal Skills' },
  { id: 'initiative',        label: 'Initiative' },
  { id: 'motivation',        label: 'Motivation / Achievement Orientation' },
  { id: 'technical',         label: 'Technical / Professional Competency' },
  { id: 'teamPlayer',        label: 'Team Player' },
  { id: 'leadership',        label: 'Leadership Skills' },
  { id: 'overallImpression', label: 'Overall Impression' },
];

function AssessCard({ children }) {
  return <div className="assess-card">{children}</div>;
}

function InterviewerAssessmentPortal({ token }) {
  const [info, setInfo]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [completed, setCompleted] = useState(false);
  const [form, setForm]         = useState({ conductedBy: '', ratings: {}, comments: '', recommendation: '' });
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');

  // ── Document attachments ──────────────────────────────────
  const [docFiles, setDocFiles] = useState([]); // [{id, file, previewUrl, name, isImage}]
  const cameraInputRef = React.useRef(null);
  const fileInputRef   = React.useRef(null);

  // Clean up object URLs on unmount
  React.useEffect(() => {
    return () => docFiles.forEach(d => { if (d.previewUrl) URL.revokeObjectURL(d.previewUrl); });
  }, [docFiles]);

  const addFiles = (fileList) => {
    const toAdd = Array.from(fileList).slice(0, 10 - docFiles.length).map(file => {
      const isImage = file.type.startsWith('image/');
      return {
        id:         Math.random().toString(36).slice(2),
        file,
        previewUrl: isImage ? URL.createObjectURL(file) : null,
        name:       file.name,
        isImage,
        sizeMB:     (file.size / 1048576).toFixed(1),
      };
    });
    if (toAdd.length) setDocFiles(prev => [...prev, ...toAdd]);
  };

  const removeDoc = (id) => {
    setDocFiles(prev => {
      const item = prev.find(d => d.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter(d => d.id !== id);
    });
  };

  useEffect(() => {
    if (!token) { setError('No assessment token found in URL.'); setLoading(false); return; }
    fetch(`${API}/assessment/${token}`)
      .then(r => r.json().then(d => ({ ok: r.ok, status: r.status, data: d })))
      .then(({ ok, status, data }) => {
        if (status === 410) { setCompleted(true); }
        else if (!ok) { setError(data.error || 'Invalid link'); }
        else {
          setInfo(data);
          setForm(f => ({ ...f, conductedBy: data.sentToName || '' }));
        }
      })
      .catch(() => setError('Could not load assessment. Check your connection.'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const setRating = (param, val) => setForm(f => ({ ...f, ratings: { ...f.ratings, [param]: val } }));

  const handleSubmit = async () => {
    setFormError('');
    if (!form.conductedBy.trim()) return setFormError('Please enter your name.');
    const allRated = ASSESS_PARAMS.every(p => form.ratings[p.id]);
    if (!allRated) return setFormError('Please rate all 8 parameters before submitting.');
    setSaving(true);
    try {
      // Use FormData so files can be attached alongside JSON fields
      const fd = new FormData();
      fd.append('conductedBy',    form.conductedBy);
      fd.append('ratings',        JSON.stringify(form.ratings));
      fd.append('comments',       form.comments);
      fd.append('recommendation', form.recommendation);
      docFiles.forEach(d => fd.append('documents', d.file, d.name));

      const res = await fetch(`${API}/assessment/${token}/submit`, {
        method: 'POST',
        body: fd,   // No Content-Type header: browser sets multipart boundary automatically
      });
      const d = await res.json();
      if (res.ok) { setCompleted(true); }
      else { setFormError(d.error || 'Submission failed. Please try again.'); }
    } catch { setFormError('Network error. Please try again.'); }
    setSaving(false);
  };

  if (loading) return (
    <AssessCard>
      <div className="assess-body" aria-hidden="true">
        <div className="sk sk-head" /><div className="sk sk-job" /><div className="sk sk-job" /><div className="sk sk-job" />
      </div>
    </AssessCard>
  );

  if (completed) return (
    <AssessCard>
      <div className="assess-body assess-center">
        <h1 className="assess-h">Assessment submitted</h1>
        <p className="muted">Thank you. Your assessment has been recorded and shared with HR.</p>
      </div>
    </AssessCard>
  );

  if (error) return (
    <AssessCard>
      <div className="assess-body assess-center">
        <h1 className="assess-h err-text" role="alert">{error}</h1>
        <p className="muted">If you believe this is a mistake, contact your HR team for a new link.</p>
      </div>
    </AssessCard>
  );

  return (
    <AssessCard>
      <div className="assess-body">
        <div className="assess-info">
          <h1 className="assess-h">Interview assessment, round {info.round}</h1>
          <p className="muted">
            <b>Candidate:</b> {info.applicantName} &nbsp;&middot;&nbsp;
            <b>Position:</b> {info.jobTitle}{info.department ? ` · ${info.department}` : ''}
          </p>
        </div>

        <div className="assess-field">
          <label className="f" htmlFor="as-name">Your name</label>
          <input id="as-name" type="text" value={form.conductedBy} onChange={e => setForm(f => ({ ...f, conductedBy: e.target.value }))}
            placeholder="Enter your full name" autoComplete="name" />
        </div>

        <div className="assess-field">
          <div className="f" id="as-rate">Rate the candidate (1 is poor, 5 is excellent)</div>
          <div className="rate-table" role="group" aria-labelledby="as-rate">
            {ASSESS_PARAMS.map(p => (
              <div key={p.id} className="rate-row">
                <span id={`r-${p.id}`}>{p.label}</span>
                <div className="rate-btns" role="radiogroup" aria-labelledby={`r-${p.id}`}>
                  {[1, 2, 3, 4, 5].map(v => (
                    <button key={v} type="button" role="radio" aria-checked={form.ratings[p.id] === v}
                      className={`rate-btn ${form.ratings[p.id] === v ? 'on' : ''}`}
                      onClick={() => setRating(p.id, v)}>{v}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="assess-field">
          <label className="f" htmlFor="as-comments">Comments and observations</label>
          <textarea id="as-comments" value={form.comments} onChange={e => setForm(f => ({ ...f, comments: e.target.value }))}
            placeholder="Any observations, strengths, concerns" rows={4} />
        </div>

        <div className="assess-field">
          <div className="f" id="as-rec">Recommendation</div>
          <div className="rec-row" role="radiogroup" aria-labelledby="as-rec">
            {[{ v: 'recommended', l: 'Recommended' }, { v: 'not_recommended', l: 'Not recommended' }].map(opt => (
              <button key={opt.v} type="button" role="radio" aria-checked={form.recommendation === opt.v}
                className={`rec-btn ${form.recommendation === opt.v ? 'on' : ''}`}
                onClick={() => setForm(f => ({ ...f, recommendation: opt.v }))}>{opt.l}</button>
            ))}
          </div>
        </div>

        <div className="assess-field docs">
          <div className="docs-head">
            <div>
              <div className="f" style={{ marginBottom: 2 }}>Attach documents <span className="muted">(optional, max 10)</span></div>
              <div className="hint">Scan IDs, notes, work samples. Images or PDF, up to 15 MB each.</div>
            </div>
            {docFiles.length > 0 && <span className="docs-count">{docFiles.length}/10</span>}
          </div>

          {docFiles.length < 10 && (
            <div className="cta" style={{ marginTop: 12 }}>
              <button type="button" className="btn soft" onClick={() => cameraInputRef.current?.click()}>Take photo</button>
              <button type="button" className="btn soft" onClick={() => fileInputRef.current?.click()}>Upload file</button>
            </div>
          )}

          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
          <input ref={fileInputRef} type="file" accept="image/*,.pdf,application/pdf" multiple style={{ display: 'none' }}
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />

          {docFiles.length > 0 && (
            <div className="doc-grid">
              {docFiles.map(doc => (
                <div key={doc.id} className="doc-tile">
                  {doc.isImage && doc.previewUrl ? (
                    <img src={doc.previewUrl} alt={doc.name} />
                  ) : (
                    <div className="doc-name">
                      <div>{doc.name.length > 20 ? doc.name.slice(0, 18) + '…' : doc.name}</div>
                      <div className="hint">{doc.sizeMB} MB</div>
                    </div>
                  )}
                  <button type="button" className="doc-x" onClick={() => removeDoc(doc.id)} aria-label={`Remove ${doc.name}`}>Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {formError && <div className="err form-err" role="alert">{formError}</div>}
        <button className="btn main wide" type="button" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Submitting' : `Submit assessment${docFiles.length ? ` and ${docFiles.length} document${docFiles.length > 1 ? 's' : ''}` : ''}`}
        </button>
        <p className="hint" style={{ textAlign: 'center', marginTop: 12 }}>This form can only be submitted once. Your response goes directly to HR.</p>
      </div>
    </AssessCard>
  );
}

// ── Routing: the candidate site ─────────────────────────────
function readRoute() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('assess')) return 'assess';                          // interviewer assessment link
  if (/^\/apply\/?$/.test(window.location.pathname)) return 'general'; // "send us your resume" link
  if (params.get('key') || params.get('joining') !== null) return 'joining';
  const hash = window.location.hash;
  if (hash.includes('?key=') || hash.includes('joining')) return 'joining';
  return 'home';
}

// Header and footer for the pages that are not the split stage (joining, assessment).
function PlainShell({ children }) {
  return (
    <div className="plain-shell">
      <header className="plain-head">
        <Link to="/" className="mark">Soch</Link>
        <nav aria-label="Careers">
          <Link to="/#roles">Open roles</Link>
          <Link to="/apply">Send resume</Link>
          <Link to="/?joining">Joining</Link>
        </nav>
      </header>
      <main className="plain-main">{children}</main>
      <div className="plain-foot"><SiteFooter /></div>
    </div>
  );
}

function PublicApp() {
  const [route, setRoute] = useState(readRoute);
  const [assessToken] = useState(() => new URLSearchParams(window.location.search).get('assess') || '');
  const { toasts } = useToast();

  useEffect(() => {
    const sync = () => {
      setRoute(readRoute());
      const id = window.location.hash.replace(/^#/, '');
      if (id && !id.includes('?')) setTimeout(() => { const el = document.getElementById(id); if (el) el.scrollIntoView(); }, 60);
    };
    window.addEventListener('soch:navigate', sync);
    window.addEventListener('popstate', sync);
    return () => { window.removeEventListener('soch:navigate', sync); window.removeEventListener('popstate', sync); };
  }, []);

  useEffect(() => {
    const titles = { home: 'Careers at Soch', general: 'Send us your resume | Soch', joining: 'Joining | Soch', assess: 'Interview assessment | Soch' };
    document.title = titles[route] || 'Soch Careers';
  }, [route]);

  return (
    <>
      {route === 'home' && <Home />}
      {route === 'general' && <GeneralApply />}
      {route === 'joining' && <PlainShell><OnboardingPortal /></PlainShell>}
      {route === 'assess' && <PlainShell><InterviewerAssessmentPortal token={assessToken} /></PlainShell>}
      <ToastContainer toasts={toasts} />
    </>
  );
}

export default function App() {
  return <PublicApp />;
}
